import React, { useEffect, useState } from "react";
import {
  Form, Input, Select, Switch, Button, Card, Row, Col,
  Typography, message, Spin, Space, Tag, Divider, Grid,
} from "antd";
import {
  SaveOutlined, ArrowLeftOutlined, EyeOutlined, StarOutlined,
} from "@ant-design/icons";
import { createPost, updatePost, getPostById, getAllAuthors } from "../utilscopy/firebase";

const { Title, Text } = Typography;
const { TextArea } = Input;
const { useBreakpoint } = Grid;

const CATEGORIES = [
  { value: "technology", label: "Technology" },
  { value: "design", label: "Design" },
  { value: "development", label: "Development" },
  { value: "productivity", label: "Productivity" },
  { value: "career", label: "Career" },
];

const READ_TIMES = ["3 min read", "5 min read", "6 min read", "7 min read",
  "8 min read", "9 min read", "10 min read", "11 min read", "15 min read"];

function slugify(str) {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");
}

export default function PostForm({ postId, onDone, onCancel }) {
  const isEdit = Boolean(postId);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [authors, setAuthors] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [tags, setTags] = useState([]);
  const [preview, setPreview] = useState(false);

  const screens = useBreakpoint();
  const isMobile = !screens.sm;

  useEffect(() => {
    async function load() {
      try {
        const a = await getAllAuthors();
        setAuthors(a);
        if (isEdit) {
          const post = await getPostById(Number(postId));
          if (!post) {
            message.error("Post not found");
            if (onCancel) onCancel();
            return;
          }
          form.setFieldsValue({ ...post, authorId: post.authorId });
          setTags(post.tags || []);
        }
      } catch (e) {
        message.error("Failed to load data");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [postId]);

  const handleTitleChange = (e) => {
    if (!isEdit) {
      form.setFieldValue("slug", slugify(e.target.value));
    }
  };

  const addTag = () => {
    const t = tagInput.trim();
    if (t && !tags.includes(t)) {
      setTags([...tags, t]);
    }
    setTagInput("");
  };

  const removeTag = (tag) => setTags(tags.filter((t) => t !== tag));

  const handleSave = async (values) => {
    setSaving(true);
    try {
      const postData = { ...values, tags };
      if (isEdit) {
        await updatePost(Number(postId), postData);
        message.success("Post updated!");
      } else {
        await createPost(postData);
        message.success("Post created!");
      }
      if (onDone) onDone();
    } catch (e) {
      console.error("Post save failed:", e);
      message.error("Save failed: " + (e.message || "Unknown error"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 24, flexWrap: "wrap" }}>
        <Button
          icon={<ArrowLeftOutlined />}
          style={{ borderColor: "#e2e8f0", borderRadius: 8 }}
          onClick={onCancel}
        >
          Back
        </Button>
        <Title level={4} style={{ margin: 0 }}>
          {isEdit ? "Edit Post" : "New Post"}
        </Title>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSave}
        initialValues={{ featured: false, category: "development" }}
      >
        <Row gutter={isMobile ? [16, 16] : [24, 24]}>
          {/* ── Left: Main Content ── */}
          <Col xs={24} lg={16}>
            <Card
              style={{ borderRadius: 16, border: "1px solid #f0f0f0", marginBottom: 20 }}
              bodyStyle={{ padding: isMobile ? 16 : 24 }}
            >
              <Form.Item
                name="title"
                label={<Text strong>Post Title</Text>}
                rules={[{ required: true, message: "Title is required" }]}
              >
                <Input
                  size="large"
                  placeholder="Enter a compelling title..."
                  onChange={handleTitleChange}
                  style={{ borderRadius: 10, fontSize: 16 }}
                />
              </Form.Item>

              <Form.Item
                name="slug"
                label={<Text strong>URL Slug</Text>}
                rules={[{ required: true, message: "Slug is required" }]}
              >
                <Input
                  placeholder="url-friendly-slug"
                  prefix={<Text type="secondary">/post/</Text>}
                  style={{ borderRadius: 10 }}
                />
              </Form.Item>

              <Form.Item
                name="excerpt"
                label={<Text strong>Excerpt</Text>}
                rules={[{ required: true, message: "Excerpt is required" }]}
              >
                <TextArea
                  rows={3}
                  placeholder="A short summary shown on cards and previews..."
                  style={{ borderRadius: 10 }}
                  maxLength={200}
                  showCount
                />
              </Form.Item>

              <Form.Item
                name="content"
                label={<Text strong>Content (Markdown-style)</Text>}
                rules={[{ required: true, message: "Content is required" }]}
              >
                <TextArea
                  rows={isMobile ? 14 : 20}
                  placeholder={`Use ## for headings, **bold** for emphasis, - for lists...\n\n## Introduction\n\nYour content here...`}
                  style={{ borderRadius: 10, fontFamily: "monospace", fontSize: 14 }}
                />
              </Form.Item>
            </Card>
          </Col>

          {/* ── Right: Meta ── */}
          <Col xs={24} lg={8}>
            {/* Publish Card */}
            <Card
              style={{ borderRadius: 16, border: "1px solid #f0f0f0", marginBottom: 20 }}
              bodyStyle={{ padding: isMobile ? 16 : 24 }}
              title={<Text strong>Publish</Text>}
            >
              <Form.Item name="featured" valuePropName="checked" style={{ marginBottom: 16 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Space>
                    <StarOutlined style={{ color: "#f59e0b" }} />
                    <Text>Featured Post</Text>
                  </Space>
                  <Switch />
                </div>
              </Form.Item>

              <Divider style={{ margin: "12px 0" }} />

              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={saving}
                block
                size="large"
                style={{
                  borderRadius: 10, background: "#111", borderColor: "#111",
                  fontWeight: 700, fontSize: 15,
                }}
              >
                {isEdit ? "Update Post" : "Publish Post"}
              </Button>
            </Card>

            {/* Meta Card */}
            <Card
              style={{ borderRadius: 16, border: "1px solid #f0f0f0", marginBottom: 20 }}
              bodyStyle={{ padding: isMobile ? 16 : 24 }}
              title={<Text strong>Post Details</Text>}
            >
              <Form.Item
                name="category"
                label="Category"
                rules={[{ required: true }]}
              >
                <Select options={CATEGORIES} style={{ borderRadius: 10 }} size="large" />
              </Form.Item>

              <Form.Item
                name="authorId"
                label="Author"
                rules={[{ required: true, message: "Select an author" }]}
              >
                <Select
                  size="large"
                  placeholder="Select author"
                  options={authors.map((a) => ({
                    value: a.id,
                    label: a.name,
                  }))}
                />
              </Form.Item>

              <Form.Item name="date" label="Published Date">
                <Input placeholder="e.g. June 10, 2025" style={{ borderRadius: 10 }} />
              </Form.Item>

              <Form.Item name="readTime" label="Read Time">
                <Select
                  size="middle"
                  placeholder="Estimated read time"
                  options={READ_TIMES.map((r) => ({ value: r, label: r }))}
                />
              </Form.Item>
            </Card>

            {/* Cover Image */}
            <Card
              style={{ borderRadius: 16, border: "1px solid #f0f0f0", marginBottom: 20 }}
              bodyStyle={{ padding: isMobile ? 16 : 24 }}
              title={<Text strong>Cover Image</Text>}
            >
              <Form.Item name="cover" noStyle>
                <Input
                  placeholder="https://images.unsplash.com/..."
                  style={{ borderRadius: 10, marginBottom: 12 }}
                  onChange={() => setPreview(true)}
                />
              </Form.Item>

              {form.getFieldValue("cover") && (
                <img
                  src={form.getFieldValue("cover")}
                  alt="cover preview"
                  style={{
                    width: "100%", height: 140, objectFit: "cover",
                    borderRadius: 10, marginTop: 8, border: "1px solid #f0f0f0",
                  }}
                  onError={(e) => (e.target.style.display = "none")}
                />
              )}
            </Card>

            {/* Tags */}
            <Card
              style={{ borderRadius: 16, border: "1px solid #f0f0f0" }}
              bodyStyle={{ padding: isMobile ? 16 : 24 }}
              title={<Text strong>Tags</Text>}
            >
              <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                <Input
                  placeholder="Add a tag..."
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onPressEnter={addTag}
                  style={{ borderRadius: 8, flex: 1 }}
                />
                <Button onClick={addTag} style={{ borderRadius: 8 }}>Add</Button>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {tags.map((tag) => (
                  <Tag
                    key={tag}
                    closable
                    onClose={() => removeTag(tag)}
                    style={{ borderRadius: 20, padding: "3px 12px", fontSize: 13 }}
                  >
                    {tag}
                  </Tag>
                ))}
                {tags.length === 0 && (
                  <Text type="secondary" style={{ fontSize: 13 }}>No tags yet</Text>
                )}
              </div>
            </Card>
          </Col>
        </Row>
      </Form>
    </div>
  );
}